import { IconAlertTriangle, IconPencil } from "@tabler/icons-react"
import { type FC, useEffect, useId, useRef, useState } from "react"

type Props = {
	label: string
	prefix: string
	value: string
	onChange(value: string): void
	placeholder: string
	maxLength: number
	warningText: string
	ariaLabel?: string
	displayValue?: string
	isReadOnly?: boolean
	readOnlyMessage?: string
	normalizeChange?(value: string): string
}
const EditableLinkField: FC<Props> = ({
	label,
	prefix,
	value,
	onChange,
	placeholder,
	maxLength,
	warningText,
	ariaLabel = "Slug",
	displayValue,
	isReadOnly = false,
	readOnlyMessage,
	normalizeChange,
}) => {
	const inputId = useId()
	const inputRef = useRef<HTMLInputElement>(null)
	const originalValueRef = useRef<string | null>(null)
	const [isEditing, setIsEditing] = useState(false)
	const displayedValue = displayValue ?? (value.trim().length > 0 ? value : placeholder)
	const inputMaxLength = normalizeChange === undefined ? maxLength : undefined

	useEffect(() => {
		if (!isEditing) return

		inputRef.current?.focus()
		inputRef.current?.select()
	}, [isEditing])

	const handleChange = (rawValue: string) => {
		const normalizedValue = normalizeChange?.(rawValue) ?? rawValue
		onChange(normalizedValue.slice(0, maxLength))
	}

	const startEditing = () => {
		originalValueRef.current = value
		setIsEditing(true)
	}

	const stopEditing = () => {
		originalValueRef.current = null
		setIsEditing(false)
	}

	const cancelEditing = () => {
		if (originalValueRef.current !== null) onChange(originalValueRef.current)
		stopEditing()
	}

	return (
		<div className="min-w-0">
			<div className="text-sm font-semibold text-gray-700">{label}</div>
			<div className="flex min-w-0 flex-wrap items-center gap-y-1 font-mono text-sm leading-7">
				<span className="min-w-0 break-all text-gray-500">{prefix}</span>
				{isReadOnly ? (
					<span className="min-w-0 rounded-md bg-gray-100 px-2 py-0.5 font-semibold text-gray-500">
						{displayedValue}
					</span>
				) : (
					<button
						type="button"
						onClick={startEditing}
						className="bg-primary/10 text-primary hover:bg-primary/15 focus:ring-primary/30 min-w-0 cursor-pointer rounded-md px-2 py-0.5 text-left font-semibold transition focus:ring-2 focus:outline-none"
					>
						<span className="break-all">{displayedValue}</span>
						<IconPencil className="ml-1 inline size-3.5 align-[-2px]" />
					</button>
				)}
			</div>
			{isReadOnly && readOnlyMessage !== undefined && (
				<div className="mt-1 text-xs text-gray-500">{readOnlyMessage}</div>
			)}
			{isEditing && !isReadOnly && (
				<div className="mt-2">
					<div className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center">
						<div className="min-w-0 flex-1">
							<input
								ref={inputRef}
								id={inputId}
								aria-label={ariaLabel}
								value={value}
								onChange={event => handleChange(event.target.value)}
								onBlur={stopEditing}
								onKeyDown={event => {
									if (event.key === "Enter") stopEditing()
									if (event.key === "Escape") {
										event.preventDefault()
										cancelEditing()
									}
								}}
								placeholder={placeholder}
								maxLength={inputMaxLength}
								className="focus:border-primary focus:ring-primary/25 w-full max-w-md rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition outline-none focus:ring-2"
							/>
						</div>
						<div className="text-xs font-medium text-gray-500">
							{value.length}/{maxLength}
						</div>
					</div>
					<div className="mt-2 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-800">
						<IconAlertTriangle className="mt-0.5 size-4 shrink-0" />
						<p className="m-0">{warningText}</p>
					</div>
				</div>
			)}
		</div>
	)
}

export default EditableLinkField
